import { Constants, userApiScope } from "@/services/web/api.const";
import { mergeParam } from "@/helper/api";
import { useApi, useApiAsync } from "@/services/web/use-api";

import { CreateServiceAccountPayload, ServiceAccount, UpdateServiceAccountPayload } from "../service-accounts.type";
import { FilterParams, PagedResult } from "../users.type";

const BASE_URL = Constants.MANAGEMENT_API_URL + "/service-accounts";

export type ServiceAccountFilterParams = FilterParams;

export const useGetServiceAccounts = (filter: ServiceAccountFilterParams, enabled = false) => {
  const options = { method: "GET", ...userApiScope };
  const url = mergeParam(BASE_URL, filter as Record<string, string | number>);

  const {
    isLoading: loading,
    error,
    data: serviceAccounts,
    refetch,
  } = useApi<PagedResult<ServiceAccount>>(url, options, ["service-accounts", filter], enabled);

  return { loading, error, serviceAccounts, refetch };
};

export const useGetServiceAccount = (id: string | undefined) => {
  const options = { method: "GET", ...userApiScope };
  const url = `${BASE_URL}/${id}`;

  const {
    isLoading: loading,
    error,
    data: serviceAccount,
    refetch,
  } = useApi<ServiceAccount>(url, options, ["service-account", id], !!id);

  return { loading, error, serviceAccount, refetch };
};

export const useCreateServiceAccount = () => {
  const url = BASE_URL;
  const options = {
    ...userApiScope,
    method: "POST",
    headers: { "Content-type": "application/json; charset=UTF-8" },
  };

  const {
    mutateAsync: createServiceAccount,
    isPending: loading,
    error,
  } = useApiAsync<ServiceAccount, CreateServiceAccountPayload>(url, options, ["service-accounts"]);

  return { createServiceAccount, loading, error };
};

export const useUpdateServiceAccount = (id: string) => {
  const url = `${BASE_URL}/${id}`;
  const options = {
    ...userApiScope,
    method: "PUT",
    headers: { "Content-type": "application/json; charset=UTF-8" },
  };

  const {
    mutateAsync: updateServiceAccount,
    isPending: loading,
    error,
  } = useApiAsync<ServiceAccount, UpdateServiceAccountPayload>(url, options, ["service-accounts"]);

  return { updateServiceAccount, loading, error };
};

export const useDeleteServiceAccount = (id: string) => {
  const url = `${BASE_URL}/${id}`;
  const options = {
    ...userApiScope,
    method: "DELETE",
  };

  const {
    mutateAsync: deleteServiceAccount,
    isPending: loading,
    error,
  } = useApiAsync<void>(url, options, ["service-accounts"]);

  return { deleteServiceAccount, loading, error };
};

export const useEnableServiceAccount = (id: string) => {
  const url = `${BASE_URL}/${id}/enable`;
  const options = {
    ...userApiScope,
    method: "PUT",
  };

  const {
    mutateAsync: enableServiceAccount,
    isPending: loading,
    error,
  } = useApiAsync<void>(url, options, ["service-accounts"]);

  return { enableServiceAccount, loading, error };
};

export const useDisableServiceAccount = (id: string) => {
  const url = `${BASE_URL}/${id}/disable`;
  const options = {
    ...userApiScope,
    method: "PUT",
  };

  const {
    mutateAsync: disableServiceAccount,
    isPending: loading,
    error,
  } = useApiAsync<void>(url, options, ["service-accounts"]);

  return { disableServiceAccount, loading, error };
};

export const useRegenerateApiKey = (id: string) => {
  const url = `${BASE_URL}/${id}/regenerate-key`;
  const options = {
    ...userApiScope,
    method: "POST",
  };

  const {
    mutateAsync: regenerateApiKey,
    isPending: loading,
    error,
  } = useApiAsync<ServiceAccount>(url, options, ["service-accounts"]);

  return { regenerateApiKey, loading, error };
};
